<template>
  <base-resize-observer @resize="handleContainerResize">
    <simplebar
      ref="simplebar"
      class="base-virtual-scroll"
      @scroll="handleScroll"
    >
      <base-virtual-scroll-viewport
        :items="items"
        :active-item-id="activeItemId"
        :context="context"
        :data-key="dataKey"
        :offset-top="offsetTop"
        :offset-bottom="offsetBottom"
        :visible-items="visibleItems"
        :scroll-top-diff="scrollTopDiff"
        :item-sizes-by-id="itemSizesById"
        :default-item-size="defaultItemSize"
        :is-scrolling="isScrolling"
        @item-activation="handleItemActivation"
        @item-deactivation="handleItemDeactivation"
        @item-resize="handleItemResize"
        @header-slot-resize="handleHeaderSlotResize"
        @footer-slot-resize="handleFooterSlotResize"
      >
        <template #default="props">
          <slot
            name="default"
            v-bind="props"
          />
        </template>

        <template #header>
          <slot name="header" />
        </template>

        <template #footer>
          <slot name="footer" />
        </template>
      </base-virtual-scroll-viewport>
    </simplebar>
  </base-resize-observer>
</template>

<script lang="ts">
import Vue from 'vue';
import { defineComponent } from '@vue/composition-api';
import Simplebar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';

import BaseVirtualScrollViewport from './BaseVirtualScrollViewport.vue';
import { BaseResizeObserver } from '../decorators';
import { useVirtualScroll } from '../../composables';

import {
  IResizeInfo,
  IVScrollItem,
  IVScrollOffsetTop,
  IVScrollOffsetBottom,
  IVScrollItemGeometry,
} from '../../types';
import { BASE_VIRTUAL_SCROLL_PROPS } from '../../constants';

const SCROLLABLE_ELEMENT_SELECTOR = '.simplebar-content-wrapper';

export default defineComponent({
  name: 'BaseVirtualScroll',
  components: {
    Simplebar,
    BaseVirtualScrollViewport,
    BaseResizeObserver,
  },
  props: {
    ...BASE_VIRTUAL_SCROLL_PROPS,
  },
  emits: ['item-resize', 'viewport-resize'],
  setup(props) {
    const {
      setActiveItemId,
      setItems,
      setItemSize,
      getItemId,
      getItemSize,
      getViewportSize,
      getItemOffsetTop,
      setScrollTop,
      setViewportSize,
      setHeaderSlotSize,
      setFooterSlotSize,
    } = useVirtualScroll(props);

    return {
      setActiveItemId,
      setItems,
      setItemSize,
      getItemId,
      getItemSize,
      getViewportSize,
      getItemOffsetTop,
      setScrollTop,
      setViewportSize,
      setHeaderSlotSize,
      setFooterSlotSize,
    };
  },
  data() {
    return {
      offsetTop: {} as IVScrollOffsetTop,
      offsetBottom: {} as IVScrollOffsetBottom,
      visibleItems: [] as IVScrollItem[],
      scrollTopDiff: 0,
      scrollableElement: null as HTMLElement | null,
      itemSizesById: {} as Record<number | string, number>,
    };
  },
  watch: {
    items() {
      this.handleItemsUpdate();
    },
    activeItemId() {
      this.setActiveItemId(this.activeItemId);
    },
    scrollTopDiff(newValue, oldValue) {
      if (oldValue === 0 && newValue !== 0) {
        this.$nextTick(() => {
          this.correctScrollTop();
          this.scrollTopDiff = 0;
        });
      }
    },
  },
  mounted() {
    const simplebarRef = this.$refs['simplebar'] as Vue;
    this.scrollableElement = simplebarRef.$el.querySelector(SCROLLABLE_ELEMENT_SELECTOR);
    this.$nextTick(() => {
      const state = this.setScrollTop(0);
      Object.assign(this, state);
    });
  },
  methods: {
    handleItemsUpdate() {
      const state = this.setItems(this.items);
      Object.assign(this, state);
    },
    handleContainerResize(size: IResizeInfo) {
      const { height } = size;
      const state = this.setViewportSize(height);
      Object.assign(this, state);
      this.$emit('viewport-resize', height);
    },

    handleScroll(event: MouseEvent): void {
      const state = this.setScrollTop((event.target as HTMLElement).scrollTop);
      Object.assign(this, state);
    },

    handleItemActivation(item: IVScrollItem): void {
      const id = item[this.dataKey];
      this.$emit('item-activation', { id });
    },

    handleItemDeactivation(item: IVScrollItem): void {
      const id = item[this.dataKey];
      this.$emit('item-deactivation', { id });
    },

    handleItemResize(item: IVScrollItem, size: IResizeInfo): void {
      /* Ignore events when item is being hidden */
      if (size.height === 0) {
        return;
      }

      const id = item[this.dataKey];
      const { height } = size;
      const state = this.setItemSize(id, height);
      Object.assign(this, state);
      this.$emit('item-resize', { id, height });
    },

    handleHeaderSlotResize(size: IResizeInfo): void {
      if (size.height !== 0) {
        const state = this.setHeaderSlotSize(size.height);
        Object.assign(this, state);
      }
    },

    handleFooterSlotResize(size: IResizeInfo): void {
      if (size.height !== 0) {
        const state = this.setFooterSlotSize(size.height);
        Object.assign(this, state);
      }
    },
    correctScrollTop() {
      if (!this.isScrolling) {
        this.scrollableElement!.scrollTop += this.scrollTopDiff;
      }
    },
    getItemGeometry(id: string): IVScrollItemGeometry | undefined {
      const pageOffsetTop = this.getItemOffsetTop(id);
      const item = this.items.find(item => item[this.dataKey] === id);
      if (!item) {
        return;
      }
      const size = this.getItemSize(item);
      return {
        size,
        pageOffsetTop,
      };
    },
    getVisibleItems(): IVScrollItem[] {
      return this.visibleItems;
    },
  },
});
</script>

<style lang="scss" scoped>
.base-virtual-scroll {
  height: 100%;
}
</style>
